import React, { useEffect, useState } from "react";
import { api, imageUrl } from "../lib/api";
import { useStores } from "../lib/stores";
import { themeById } from "../lib/storeThemes";
import { Plus, Trash2, Building2, Star, Pencil } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const empty = { name: "", address: "", phone: "", is_default: false };

export default function Stores() {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(empty);
  const { stores, reload } = useStores();

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/stores", form);
      toast.success("Store added");
      setForm(empty); setShowAdd(false); reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this store?")) return;
    await api.delete(`/stores/${id}`); reload();
  };

  const makeDefault = async (s) => {
    await api.put(`/stores/${s.id}`, { ...s, is_default: true });
    toast.success("Default store updated");
    reload();
  };

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto" data-testid="stores-root">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="font-display font-bold text-3xl">Stores</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>Manage one or more store locations under this account. Switch active store from the sidebar.</p>
        </div>
        <button data-testid="stores-add-btn" onClick={() => setShowAdd(true)} className="inline-flex items-center gap-2 px-5 h-12 rounded-md text-white tactile-btn font-semibold" style={{ background: "var(--primary)" }}>
          <Plus size={18} /> Add store
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {stores.map((s) => (
          <div key={s.id} className="card-soft p-5" data-testid={`store-row-${s.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                {s.image ? (
                  <img src={imageUrl(s.image)} alt="" className="w-10 h-10 rounded-md object-cover shrink-0" />
                ) : (
                  <div className="w-10 h-10 rounded-md grid place-items-center shrink-0" style={{ background: "var(--surface-tint)", color: "var(--primary)" }}><Building2 size={20} /></div>
                )}
                <div className="min-w-0">
                  <div className="font-display font-semibold text-lg truncate">{s.name}</div>
                  <div className="text-xs truncate" style={{ color: "var(--text-muted)" }}>
                    {[s.city, s.state].filter(Boolean).join(", ") || s.address || "No address"}
                    {s.phone && ` • ${s.phone}`}
                  </div>
                  {s.gst_number && <div className="text-xs font-mono mt-0.5" style={{ color: "var(--text-muted)" }}>GSTIN {s.gst_number}</div>}
                  {s.theme && (
                    <div className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>
                      Theme: {themeById(s.theme).name}
                    </div>
                  )}
                </div>
              </div>
              {s.is_default && <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full shrink-0" style={{ background: "var(--surface-alert)", color: "var(--warning)" }}><Star size={12} /> Default</span>}
            </div>
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <Link
                to={`/app/stores/${s.id}/profile`}
                data-testid={`store-profile-${s.id}`}
                className="h-9 px-3 rounded-md text-sm border inline-flex items-center gap-1"
                style={{ borderColor: "var(--primary)", color: "var(--primary)" }}
              >
                <Pencil size={14} /> Edit profile
              </Link>
              {!s.is_default && <button data-testid={`store-default-${s.id}`} onClick={() => makeDefault(s)} className="h-9 px-3 rounded-md text-sm border" style={{ borderColor: "var(--border-soft)" }}>Make default</button>}
              <button data-testid={`store-del-${s.id}`} onClick={() => del(s.id)} className="h-9 px-3 rounded-md text-sm inline-flex items-center gap-1" style={{ color: "var(--error)" }}><Trash2 size={14} /> Delete</button>
            </div>
          </div>
        ))}
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setShowAdd(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white rounded-xl p-6 w-full max-w-lg">
            <h2 className="font-display font-bold text-xl mb-4">Add store</h2>
            <div className="grid gap-3">
              <F label="Store name*" required v={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="add-store-name" />
              <F label="Address" v={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="add-store-address" />
              <F label="Phone" v={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="add-store-phone" />
              <label className="flex items-center gap-2 text-sm mt-1">
                <input data-testid="add-store-default" type="checkbox" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} />
                Set as default store
              </label>
            </div>
            <div className="flex gap-3 mt-5">
              <button type="button" onClick={() => setShowAdd(false)} className="h-11 px-5 rounded-md border" style={{ borderColor: "var(--border-soft)" }}>Cancel</button>
              <button data-testid="add-store-save" className="h-11 px-6 rounded-md text-white tactile-btn font-semibold" style={{ background: "var(--primary)" }}>Save</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function F({ label, v, onChange, required, testid }) {
  return (
    <div>
      <label className="text-sm font-medium">{label}</label>
      <input data-testid={testid} required={required} value={v} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full h-11 px-3 rounded-md border bg-white" style={{ borderColor: "var(--border-soft)" }} />
    </div>
  );
}
